/**
 * Composable useInteriorScene - Gestion de la scène intérieure 360° avec Three.js
 * Basé sur l'analyse de InteriorView.js et InteriorView2.js
 */

import { ref, type Ref } from 'vue'
import * as THREE from 'three'
import {
  Scene,
  PerspectiveCamera,
  WebGLRenderer,
  CubeTexture
} from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { loadCubeMap, type CubeMapLoadProgress } from '@/utils/cubeMapLoader'
import { DEFAULT_CAMERA_CONFIG, DEFAULT_CONTROLS_CONFIG, type CubeMapFaces, type UseInteriorSceneOptions } from '@/types/interior.types'

/** Valeur de retour du composable */
export interface UseInteriorSceneReturn {
  /** Indique si la scène est prête */
  isReady: Ref<boolean>
  /** Indique si le chargement est en cours */
  isLoading: Ref<boolean>
  /** Progression du chargement (0-100) */
  loadProgress: Ref<number>
  /** Message d'erreur éventuel */
  error: Ref<string | null>
  /** Initialiser la scène avec un conteneur */
  init: (container: HTMLElement) => void
  /** Charger un cubemap */
  loadCubeMap: (faces: CubeMapFaces | string[]) => Promise<void>
  /** Démarrer l'animation */
  startAnimation: () => void
  /** Arrêter l'animation */
  stopAnimation: () => void
  /** Activer le mode plein framerate */
  activate: () => void
  /** Désactiver (mode throttlé) */
  deactivate: () => void
  /** Nettoyer les ressources */
  dispose: () => void
}

/**
 * Composable pour gérer la scène intérieure 360° avec Three.js
 * 
 * Fonctionnalités :
 * - Scène Three.js avec PerspectiveCamera et OrbitControls
 * - Chargement de cubemap avec rotation de la face bottom
 * - Animation avec throttling (plein FPS vs 5 FPS)
 * - Responsive automatique avec ResizeObserver
 * - Gestion mémoire avec dispose()
 * 
 * @param options - Options de configuration
 * @returns Interface de contrôle de la scène
 * 
 * @example
 * ```typescript
 * const { isReady, init, loadCubeMap, activate, dispose } = useInteriorScene({
 *   autoAnimate: true,
 *   throttledFps: 5
 * })
 * 
 * onMounted(async () => {
 *   init(containerRef.value)
 *   await loadCubeMap({
 *     right: '/assets/interior/right.jpg',
 *     left: '/assets/interior/left.jpg',
 *     // ... autres faces
 *   })
 * })
 * 
 * onUnmounted(() => {
 *   dispose()
 * })
 * ```
 */
export function useInteriorScene(
  options: UseInteriorSceneOptions = {}
): UseInteriorSceneReturn {
  const {
    camera: cameraConfig,
    controls: controlsConfig,
    autoAnimate = true,
    throttledFps = 5
  } = options

  // Utiliser les configs par défaut depuis types
  const finalCameraConfig = { ...DEFAULT_CAMERA_CONFIG, ...cameraConfig }
  const finalControlsConfig = { ...DEFAULT_CONTROLS_CONFIG, ...controlsConfig }

  // États réactifs
  const isReady = ref<boolean>(false)
  const isLoading = ref<boolean>(false)
  const loadProgress = ref<number>(0)
  const error = ref<string | null>(null)

  // Objets Three.js (non réactifs)
  let scene: Scene | null = null
  let camera: PerspectiveCamera | null = null
  let renderer: WebGLRenderer | null = null
  let controls: OrbitControls | null = null
  let cubeTexture: CubeTexture | null = null
  let container: HTMLElement | null = null
  let resizeObserver: ResizeObserver | null = null

  // Gestion de l'animation
  let animationId: number | null = null
  let isActive = false
  let lastFrameTime = 0
  const throttleInterval = 1000 / throttledFps // ms entre chaque frame en mode throttlé

  /**
   * Initialiser la scène Three.js
   */
  const init = (containerElement: HTMLElement): void => {
    if (!containerElement) {
      error.value = 'Conteneur invalide'
      return
    }

    container = containerElement

    try {
      // Créer la scène
      scene = new Scene()

      // Créer la caméra
      camera = new PerspectiveCamera(
        finalCameraConfig.fov,
        containerElement.clientWidth / containerElement.clientHeight,
        finalCameraConfig.near,
        finalCameraConfig.far
      )
      camera.position.set(
        finalCameraConfig.position.x,
        finalCameraConfig.position.y,
        finalCameraConfig.position.z
      )

      // Créer le renderer avec LinearColorSpace pour éviter la saturation des couleurs
      // Note: outputColorSpace et toneMapping doivent être définis après l'instanciation
      // dans les versions récentes de Three.js (r160+)
      renderer = new WebGLRenderer({ 
        antialias: true, 
        alpha: true
      })
      renderer.outputColorSpace = THREE.LinearSRGBColorSpace
      renderer.toneMapping = THREE.NoToneMapping
      renderer.setSize(containerElement.clientWidth, containerElement.clientHeight)
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
      containerElement.appendChild(renderer.domElement)

      // Créer les contrôles orbitaux
      controls = new OrbitControls(camera, renderer.domElement)
      controls.enableZoom = finalControlsConfig.enableZoom
      controls.enablePan = finalControlsConfig.enablePan
      controls.enableDamping = finalControlsConfig.enableDamping
      controls.dampingFactor = finalControlsConfig.dampingFactor
      controls.minPolarAngle = finalControlsConfig.minPolarAngle
      controls.maxPolarAngle = finalControlsConfig.maxPolarAngle
      controls.rotateSpeed = finalControlsConfig.rotateSpeed

      // Configurer le ResizeObserver pour le responsive
      setupResizeObserver()

      // Démarrer l'animation si autoAnimate
      if (autoAnimate) {
        startAnimation()
      }

      isReady.value = true
      error.value = null
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Erreur inconnue'
      error.value = `Erreur d'initialisation: ${errMsg}`
      console.error('Erreur init scene:', err)
    }
  }

  /**
   * Configurer le ResizeObserver pour gérer le responsive
   */
  const setupResizeObserver = (): void => {
    if (!container || !camera || !renderer) return

    let resizeCount = 0
    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        resizeCount++
        
        console.log(`[DEBUG useInteriorScene] ResizeObserver déclenché (#${resizeCount}):`, {
          width,
          height,
          rendererSize: renderer ? { width: renderer.domElement.width, height: renderer.domElement.height } : null,
          containerSize: container ? { width: container.clientWidth, height: container.clientHeight } : null
        })

        if (width > 0 && height > 0 && camera && renderer) {
          // Mettre à jour l'aspect de la caméra
          camera.aspect = width / height
          camera.updateProjectionMatrix()

          // Mettre à jour la taille du renderer
          // IMPORTANT : updateStyle = false pour éviter la boucle de resize infinie
          // Le canvas va se dimensionner automatiquement via CSS
          renderer.setSize(width, height, false)
          
          console.log('[DEBUG useInteriorScene] Après setSize (updateStyle=false):', {
            rendererSize: { width: renderer.domElement.width, height: renderer.domElement.height },
            rendererStyle: { width: renderer.domElement.style.width, height: renderer.domElement.style.height }
          })
        }
      }
    })

    resizeObserver.observe(container)
    console.log('[DEBUG useInteriorScene] ResizeObserver créé et attaché au container')
  }

  /**
   * Charger un cubemap
   */
  const loadCubeMapFn = async (faces: CubeMapFaces | string[]): Promise<void> => {
    if (!scene) {
      error.value = 'La scène doit être initialisée avant de charger le cubemap'
      return
    }

    isLoading.value = true
    loadProgress.value = 0
    error.value = null

    try {
      cubeTexture = await loadCubeMap(faces, {
        onProgress: (progress: CubeMapLoadProgress) => {
          loadProgress.value = progress.percentage
        },
        onError: (err: Error) => {
          error.value = err.message
        }
      })

      // Appliquer la texture en tant qu'environnement
      scene.background = cubeTexture

      loadProgress.value = 100
      isLoading.value = false
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Erreur inconnue'
      error.value = `Erreur de chargement: ${errMsg}`
      isLoading.value = false
    }
  }

  /**
   * Boucle d'animation
   */
  const animate = (timestamp: number): void => {
    if (!scene || !camera || !renderer || !controls) return

    // Throttling: limiter le framerate quand inactif
    if (!isActive) {
      const elapsed = timestamp - lastFrameTime
      if (elapsed < throttleInterval) {
        animationId = requestAnimationFrame(animate)
        return
      }
      lastFrameTime = timestamp
    }

    // Mettre à jour les contrôles (pour le damping)
    if (controls.enableDamping) {
      controls.update()
    }

    // Rendu
    renderer.render(scene, camera)

    // Continuer l'animation
    animationId = requestAnimationFrame(animate)
  }

  /**
   * Démarrer l'animation
   */
  const startAnimation = (): void => {
    if (animationId !== null) {
      return // Déjà en cours
    }

    lastFrameTime = performance.now()
    animationId = requestAnimationFrame(animate)
  }

  /**
   * Arrêter l'animation
   */
  const stopAnimation = (): void => {
    if (animationId !== null) {
      cancelAnimationFrame(animationId)
      animationId = null
    }
  }

  /**
   * Activer le mode plein framerate (sur hover par exemple)
   */
  const activate = (): void => {
    isActive = true
  }

  /**
   * Désactiver (mode throttlé)
   */
  const deactivate = (): void => {
    isActive = false
  }

  /**
   * Nettoyer les ressources
   */
  const dispose = (): void => {
    // Arrêter l'animation
    stopAnimation()

    // Nettoyer le ResizeObserver
    if (resizeObserver && container) {
      resizeObserver.unobserve(container)
      resizeObserver.disconnect()
      resizeObserver = null
    }

    // Nettoyer les contrôles
    if (controls) {
      controls.dispose()
      controls = null
    }

    // Nettoyer le renderer
    if (renderer) {
      if (container && renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement)
      }
      renderer.dispose()
      renderer = null
    }

    // Nettoyer la texture
    if (cubeTexture) {
      cubeTexture.dispose()
      cubeTexture = null
    }

    // Réinitialiser les références
    scene = null
    camera = null
    container = null

    // Réinitialiser les états
    isReady.value = false
    isLoading.value = false
    loadProgress.value = 0
    error.value = null
  }

  return {
    isReady,
    isLoading,
    loadProgress,
    error,
    init,
    loadCubeMap: loadCubeMapFn,
    startAnimation,
    stopAnimation,
    activate,
    deactivate,
    dispose
  }
}
